import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateRentalMutation } from '../../../../hooks/mutations/useRentalMutation';
import { useRentalsQuery } from '../../../../hooks/queries/useRentalsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { Rental } from '../../../../services/apiSLTP/materials/rentals';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getStatusColor } from '../../../../utils/colors';
import { crudStatus, getCrudStatusInfos, localStorageKeys, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import UpdateRentalModal from '../../../modals/admin/external/UpdateRentalModal';
import DeleteModal from '../../../modals/DeleteModal';
import GenericAgGridTable from '../../GenericAgGridTable';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import IconButton from '../../../shared/IconButton';
import { createDateColumn, createDateTimeColumn } from '../../../../utils/dates';

interface RentalsSettingsRowProps {
    withUsageCount: boolean;
}

const RentalsSettingsRow: React.FC<RentalsSettingsRowProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRental, setSelectedRental] = useState<Rental | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();

    const { data: dataRentals, isLoading, refetch } = useRentalsQuery({ queries: { withUsage: withUsageCount } });

    const { mutate: mutateRental } = useUpdateRentalMutation({
        onSettled: () => refetch(),
        onError: (error) => console.error('Error updating rental:', error.message),
    });

    const columnDefs: ColDef<Rental>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        { headerName: capitalizeFirstLetter(t('codeProC')), field: 'codeProC', colId: 'codeProC', initialWidth: 150 },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Rental>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyRentalType'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedRental(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatus.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevels.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteRentalType'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedRental(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatus.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusInfos(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Rental>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getStatusColor(params.data?.crudStatus)} text={t(getCrudStatusInfos(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('email')),
            field: 'email',
            colId: 'email',
        },
        {
            headerName: capitalizeFirstLetter(t('displayOrder')),
            field: 'displayOrder',
            colId: 'displayOrder',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        createDateTimeColumn<Rental>('createdAt', 'createdAt'),
        createDateTimeColumn<Rental>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevels.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                colId: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xTransfersPlanned')),
                field: 'usageCount.dailyTransfersPlanned',
                colId: 'xtransfersplanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xTransfersCompleted')),
                field: 'usageCount.dailyTransfersRides',
                colId: 'xtransferscompleted',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRentals')),
                field: 'usageCount.dailyRentals',
                colId: 'xrentals',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    const sortedRentals = useMemo(() => {
        return dataRentals?.sort((a, b) => a.designation.localeCompare(b.designation)) || [];
    }, [dataRentals]);

    return (
        <>
            <GenericAgGridTable
                rowData={sortedRentals}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-rentals"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedRental(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addRental'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.EXTERNAL.RENTALS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateRentalModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    isEditMode={isEditMode}
                    rental={selectedRental}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && selectedRental && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateRental({
                            rentalId: selectedRental._id,
                            data: { crudStatus: crudStatus.DELETED },
                        });
                    }}
                    title={t('rental')}
                    itemName={selectedRental.designation}
                />
            )}
        </>
    );
};

export default RentalsSettingsRow;
